import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePeriod, type Period } from '../lib/period';
import { Badge, Card, CardHeader, EmptyState, PageHeader, Table, Tabs, Td, Th, Tr } from '../components/ui';
import { PeriodLine, useNumber } from '../components/kit';
import { AXIS, ChartFrame, GRID, SERIES, legendText, tooltipItemStyle, tooltipLabelStyle, tooltipStyle } from '../components/b-charts';
import { FilterRow, Loaded, Note, ParamSelect, PersonLink, Stat, StatGrid, TextLink, sourceHref, useFilterOptions, useHours, usePercent } from '../components/b-shared';

/**
 * Work & Productivity (CR-008 §8): tasks, tickets, Q Tasks and workflows, each work item counted once —
 * a workflow's tasks are its steps, a converted Q Task counts through its tasks — with Leaders Tasks apart.
 * Departments are each set against their own previous period, never against one another; workload is the
 * open work each person holds, never "utilization": the system keeps no time estimates.
 */

const TABS = ['summary', 'departments', 'workload'] as const;
type Tab = (typeof TABS)[number];
/** The stat columns of a card that sits half-width beside another on a wide screen. */
const HALF = 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3';

interface WorkOverview {
  period: Period;
  tasks: {
    created: number;
    completed: number;
    uniqueRootItemsCompleted: number;
    workflowStepsCompleted: number;
    fromQTasks: number;
    cancelled: number;
    open: { toDo: number; inProgress: number; blocked: number; review: number };
    overdue: number;
    dueSoon: number;
    cycleTime: { basis: string; sample: number; medianHours: number | null; p90Hours: number | null };
  };
  tickets: {
    created: number;
    resolvedTickets: number;
    resolutionEvents: number;
    reopenEvents: number;
    closed: number;
    open: { new: number; assigned: number; inProgress: number; waiting: number; reopened: number };
    urgentOpen: number;
    highOpen: number;
    urgentOrHighUnassigned: number;
    unassignedPastDeadline: number;
    openViolations: number;
    viewSla: { decided: number; onTime: number; compliancePercent: number | null };
    assignmentSla: { decided: number; onTime: number; compliancePercent: number | null };
    reopenRatePercent: number | null;
  };
  qTasks: {
    available: number;
    claimed: number;
    inProgress: number;
    blocked: number;
    doneStandalone: number;
    converted: number;
    skipped: number;
    queueAgeMedianHours: number | null;
    blockedLong: Array<{ id: string; number: string; blockedDays: number }>;
    blockedLongCount: number;
  };
  workflows: {
    active: number;
    completed: number;
    stopped: number;
    stepsCompleted: number;
    stalled: Array<{ id: string; name: string; nameAr: string | null; level: number | null; idleDays: number; blocked: boolean }>;
    stalledCount: number;
  };
  leaderTasks: { open: number; done: number };
  newlyOverdue: number;
}

interface DepartmentRow {
  department: { id: string; name: string; nameAr: string | null; code: string };
  members: number;
  backlog: { tasks: number; tickets: number; qTasks: number };
  overdueTasks: number;
  completed: { tasks: number; tickets: number; qTasks: number; total: number };
  previousCompleted: number;
  changePercent: number | null;
}

interface WorkloadRow {
  user: { id: string; fullName: string; fullNameAr: string | null; jobTitle: string | null };
  openTasks: number;
  overdue: number;
  dueSoon: number;
  openTickets: number;
  openQTasks: number;
}

export function WorkPage() {
  const { t, localized } = useI18n();
  const { param, setParam } = usePeriod();
  const filters = useFilterOptions();
  const tab: Tab = (TABS as readonly string[]).includes(param('tab')) ? (param('tab') as Tab) : 'summary';
  return (
    <>
      <PageHeader title={t('work.title')} subtitle={t('work.subtitle')} />
      <FilterRow>
        <ParamSelect
          param="departmentId"
          label={t('common.department')}
          allLabel={t('employees.allDepartments')}
          options={(filters.data?.departments ?? []).map((d) => ({ value: d.id, label: localized(d) }))}
        />
        <ParamSelect
          param="projectId"
          label={t('common.project')}
          allLabel={t('work.allProjects')}
          options={(filters.data?.projects ?? []).map((p) => ({ value: p.id, label: `${p.code} · ${localized(p)}` }))}
        />
      </FilterRow>
      {param('projectId') && <Note className="-mt-3 mb-4">{t('work.projectFilterNote')}</Note>}
      <Tabs tabs={TABS.map((id) => ({ id, label: t(`work.tab.${id}`) }))} active={tab} onChange={(id) => setParam('tab', id === 'summary' ? null : id)} />
      <div className="mt-5">
        {tab === 'summary' && <SummaryTab />}
        {tab === 'departments' && <DepartmentsTab />}
        {tab === 'workload' && <WorkloadTab />}
      </div>
    </>
  );
}

/** The filters every tab reads, as the API takes them. */
function useScope() {
  const { params, param } = usePeriod();
  const departmentId = param('departmentId');
  const projectId = param('projectId');
  return { params, departmentId, projectId, request: { ...params, departmentId, projectId } };
}

/* ---------------------------------------------------------------------------------------------- */

function Group({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

function SubHeading({ children }: { children: ReactNode }) {
  return <h4 className="mb-2 text-caption font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{children}</h4>;
}

function SummaryTab() {
  const { t } = useI18n();
  const scope = useScope();
  const overview = useQuery({
    queryKey: ['executive', 'work', 'overview', scope.params, scope.departmentId, scope.projectId],
    queryFn: () => api.get<WorkOverview>('/executive/work/overview', scope.request),
    placeholderData: keepPreviousData,
  });
  return (
    <Loaded query={overview}>
      {(data) => (
        <div className="space-y-5">
          <PeriodLine period={data.period} />
          <Note>{t('work.countedOnce')}</Note>
          <TasksGroup data={data} />
          <TicketsGroup data={data} />
          <div className="grid gap-5 xl:grid-cols-2">
            <QTasksGroup data={data} />
            <WorkflowsGroup data={data} />
          </div>
          <Group title={t('work.leaders.title')} subtitle={t('work.leaders.subtitle')}>
            <StatGrid>
              <LeaderStats data={data} />
            </StatGrid>
          </Group>
        </div>
      )}
    </Loaded>
  );
}

function LeaderStats({ data }: { data: WorkOverview }) {
  const { t } = useI18n();
  const num = useNumber();
  return (
    <>
      <Stat label={t('work.leaders.open')} value={num(data.leaderTasks.open)} hint={t('work.nowHint')} />
      <Stat label={t('work.leaders.done')} value={num(data.leaderTasks.done)} hint={t('work.inPeriodHint')} />
    </>
  );
}

function TasksGroup({ data }: { data: WorkOverview }) {
  const { t } = useI18n();
  const num = useNumber();
  const hours = useHours();
  const k = data.tasks;
  return (
    <Group title={t('work.tasks.title')} subtitle={t('work.tasks.subtitle')}>
      <div>
        <SubHeading>{t('work.inPeriod')}</SubHeading>
        <StatGrid>
          <Stat label={t('work.tasks.uniqueRoot')} value={num(k.uniqueRootItemsCompleted)} hint={t('work.tasks.uniqueRootHint')} />
          <Stat label={t('work.tasks.steps')} value={num(k.workflowStepsCompleted)} hint={t('work.tasks.stepsHint')} />
          <Stat label={t('work.tasks.completed')} value={num(k.completed)} hint={t('work.tasks.fromQTasks', { n: num(k.fromQTasks) })} />
          <Stat label={t('work.tasks.created')} value={num(k.created)} />
          <Stat label={t('work.tasks.cancelled')} value={num(k.cancelled)} />
          <Stat label={t('work.tasks.newlyOverdue')} value={num(data.newlyOverdue)} hint={t('work.tasks.newlyOverdueHint')} tone={data.newlyOverdue > 0 ? 'warning' : 'neutral'} />
        </StatGrid>
      </div>
      <div>
        <SubHeading>{t('work.now')}</SubHeading>
        <StatGrid>
          <Stat label={t('work.tasks.open.toDo')} value={num(k.open.toDo)} />
          <Stat label={t('work.tasks.open.inProgress')} value={num(k.open.inProgress)} />
          <Stat label={t('work.tasks.open.blocked')} value={num(k.open.blocked)} tone={k.open.blocked > 0 ? 'warning' : 'neutral'} />
          <Stat label={t('work.tasks.open.review')} value={num(k.open.review)} />
          <Stat label={t('work.tasks.overdue')} value={num(k.overdue)} hint={t('work.tasks.overdueHint')} tone={k.overdue > 0 ? 'error' : 'neutral'} />
          <Stat label={t('work.tasks.dueSoon')} value={num(k.dueSoon)} hint={t('work.tasks.dueSoonHint')} />
        </StatGrid>
      </div>
      <div>
        <SubHeading>{t('work.tasks.cycle.title')}</SubHeading>
        <StatGrid>
          <Stat label={t('work.tasks.cycle.median')} value={hours(k.cycleTime.medianHours)} />
          <Stat label={t('work.tasks.cycle.p90')} value={hours(k.cycleTime.p90Hours)} />
          <Stat label={t('work.tasks.cycle.sample')} value={num(k.cycleTime.sample)} />
        </StatGrid>
        <Note className="mt-2">{t('work.tasks.cycle.note')}</Note>
      </div>
    </Group>
  );
}

function TicketsGroup({ data }: { data: WorkOverview }) {
  const { t } = useI18n();
  const num = useNumber();
  const pct = usePercent();
  const k = data.tickets;
  return (
    <Group title={t('work.tickets.title')} subtitle={t('work.tickets.subtitle')}>
      <div>
        <SubHeading>{t('work.inPeriod')}</SubHeading>
        <StatGrid>
          <Stat label={t('work.tickets.created')} value={num(k.created)} />
          <Stat label={t('work.tickets.resolvedTickets')} value={num(k.resolvedTickets)} hint={t('work.tickets.resolvedTicketsHint')} />
          <Stat label={t('work.tickets.resolutionEvents')} value={num(k.resolutionEvents)} hint={t('work.tickets.resolutionEventsHint')} />
          <Stat label={t('work.tickets.reopenEvents')} value={num(k.reopenEvents)} tone={k.reopenEvents > 0 ? 'warning' : 'neutral'} />
          <Stat label={t('work.tickets.reopenRate')} value={pct(k.reopenRatePercent)} hint={t('work.tickets.reopenRateHint')} />
          <Stat label={t('work.tickets.closed')} value={num(k.closed)} />
          <Stat
            label={t('work.tickets.viewSla')}
            value={pct(k.viewSla.compliancePercent)}
            hint={k.viewSla.decided ? t('work.tickets.slaHint', { onTime: num(k.viewSla.onTime), decided: num(k.viewSla.decided) }) : t('work.tickets.slaNone')}
          />
          <Stat
            label={t('work.tickets.assignmentSla')}
            value={pct(k.assignmentSla.compliancePercent)}
            hint={k.assignmentSla.decided ? t('work.tickets.slaHint', { onTime: num(k.assignmentSla.onTime), decided: num(k.assignmentSla.decided) }) : t('work.tickets.slaNone')}
          />
        </StatGrid>
      </div>
      <div>
        <SubHeading>{t('work.now')}</SubHeading>
        <StatGrid>
          <Stat label={t('work.tickets.open.new')} value={num(k.open.new)} />
          <Stat label={t('work.tickets.open.assigned')} value={num(k.open.assigned)} />
          <Stat label={t('work.tickets.open.inProgress')} value={num(k.open.inProgress)} />
          <Stat label={t('work.tickets.open.waiting')} value={num(k.open.waiting)} />
          <Stat label={t('work.tickets.open.reopened')} value={num(k.open.reopened)} tone={k.open.reopened > 0 ? 'warning' : 'neutral'} />
          <Stat label={t('work.tickets.urgentOpen')} value={num(k.urgentOpen)} />
          <Stat label={t('work.tickets.highOpen')} value={num(k.highOpen)} />
          <Stat label={t('work.tickets.urgentHighUnassigned')} value={num(k.urgentOrHighUnassigned)} tone={k.urgentOrHighUnassigned > 0 ? 'error' : 'neutral'} />
          <Stat label={t('work.tickets.unassignedPastDeadline')} value={num(k.unassignedPastDeadline)} tone={k.unassignedPastDeadline > 0 ? 'error' : 'neutral'} />
          <Stat label={t('work.tickets.openViolations')} value={num(k.openViolations)} hint={t('work.tickets.openViolationsHint')} />
        </StatGrid>
      </div>
    </Group>
  );
}

function QTasksGroup({ data }: { data: WorkOverview }) {
  const { t } = useI18n();
  const { query } = usePeriod();
  const num = useNumber();
  const hours = useHours();
  const k = data.qTasks;
  return (
    <Group title={t('work.qTasks.title')} subtitle={t('work.qTasks.subtitle')}>
      <div>
        <SubHeading>{t('work.now')}</SubHeading>
        <StatGrid columns={HALF}>
          <Stat label={t('work.qTasks.available')} value={num(k.available)} />
          <Stat label={t('work.qTasks.claimed')} value={num(k.claimed)} />
          <Stat label={t('work.qTasks.inProgress')} value={num(k.inProgress)} />
          <Stat label={t('work.qTasks.blocked')} value={num(k.blocked)} tone={k.blocked > 0 ? 'warning' : 'neutral'} />
          <Stat label={t('work.qTasks.queueAge')} value={hours(k.queueAgeMedianHours)} hint={t('work.qTasks.queueAgeHint')} />
        </StatGrid>
      </div>
      <div>
        <SubHeading>{t('work.inPeriod')}</SubHeading>
        <StatGrid columns={HALF}>
          <Stat label={t('work.qTasks.doneStandalone')} value={num(k.doneStandalone)} hint={t('work.qTasks.doneStandaloneHint')} />
          <Stat label={t('work.qTasks.converted')} value={num(k.converted)} hint={t('work.qTasks.convertedHint')} />
          <Stat label={t('work.qTasks.skipped')} value={num(k.skipped)} />
        </StatGrid>
      </div>
      <div>
        <SubHeading>{t('work.qTasks.longBlocked', { n: num(k.blockedLongCount) })}</SubHeading>
        {k.blockedLong.length === 0 ? (
          <p className="text-body-sm text-muted">{t('work.qTasks.longBlockedNone')}</p>
        ) : (
          <ul className="divide-y divide-[var(--line)] rounded-md border hairline">
            {k.blockedLong.map((q) => {
              const href = sourceHref('Q_TASK', q.id, query);
              return (
                <li key={q.id} className="flex items-center justify-between gap-3 px-3 py-2 text-body-sm">
                  {href ? (
                    <TextLink to={href}>
                      <span dir="ltr" className="font-mono">
                        {q.number}
                      </span>
                    </TextLink>
                  ) : (
                    <span dir="ltr" className="font-mono">
                      {q.number}
                    </span>
                  )}
                  <Badge tone="warning">{t('work.daysBlocked', { n: num(q.blockedDays) })}</Badge>
                </li>
              );
            })}
          </ul>
        )}
        {k.blockedLongCount > k.blockedLong.length && <Note className="mt-2">{t('work.firstOf', { shown: num(k.blockedLong.length), total: num(k.blockedLongCount) })}</Note>}
      </div>
    </Group>
  );
}

function WorkflowsGroup({ data }: { data: WorkOverview }) {
  const { t, localized } = useI18n();
  const num = useNumber();
  const k = data.workflows;
  return (
    <Group title={t('work.workflows.title')} subtitle={t('work.workflows.subtitle')}>
      <StatGrid columns={HALF}>
        <Stat label={t('work.workflows.active')} value={num(k.active)} hint={t('work.nowHint')} />
        <Stat label={t('work.workflows.completed')} value={num(k.completed)} hint={t('work.inPeriodHint')} />
        <Stat label={t('work.workflows.stopped')} value={num(k.stopped)} hint={t('work.inPeriodHint')} />
        <Stat label={t('work.workflows.steps')} value={num(k.stepsCompleted)} hint={t('work.inPeriodHint')} />
      </StatGrid>
      <div>
        <SubHeading>{t('work.workflows.stalled', { n: num(k.stalledCount) })}</SubHeading>
        {k.stalled.length === 0 ? (
          <p className="text-body-sm text-muted">{t('work.workflows.stalledNone')}</p>
        ) : (
          <ul className="divide-y divide-[var(--line)] rounded-md border hairline">
            {k.stalled.map((w) => (
              <li key={w.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-body-sm">
                <span className="min-w-0">
                  <span className="text-[var(--text-primary)]">{localized(w)}</span>
                  {w.level !== null && <span className="ms-2 text-caption text-muted">{t('work.workflows.level', { n: num(w.level) })}</span>}
                </span>
                <span className="flex items-center gap-1.5">
                  {w.blocked && <Badge tone="warning">{t('work.workflows.hasBlocked')}</Badge>}
                  <Badge tone="neutral">{t('work.workflows.idle', { n: num(w.idleDays) })}</Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
        {k.stalledCount > k.stalled.length && <Note className="mt-2">{t('work.firstOf', { shown: num(k.stalled.length), total: num(k.stalledCount) })}</Note>}
      </div>
    </Group>
  );
}

/* ---------------------------------------------------------------------------------------------- */

function DepartmentsTab() {
  const { t, localized } = useI18n();
  const num = useNumber();
  const scope = useScope();
  const departments = useQuery({
    queryKey: ['executive', 'work', 'departments', scope.params, scope.departmentId, scope.projectId],
    queryFn: () => api.get<{ period: Period; rows: DepartmentRow[] }>('/executive/work/departments', scope.request),
    placeholderData: keepPreviousData,
  });
  return (
    <Loaded query={departments}>
      {(data) => {
        const names = { backlog: t('work.departments.backlog'), completed: t('work.departments.completed'), previous: t('work.departments.previous') };
        const chartRows = data.rows.map((r) => ({
          code: r.department.code,
          name: localized(r.department),
          backlog: r.backlog.tasks + r.backlog.tickets + r.backlog.qTasks,
          completed: r.completed.total,
          previous: r.previousCompleted,
        }));
        return (
          <div className="space-y-5">
            <PeriodLine period={data.period} />
            <Note>{t('work.departments.note')}</Note>
            {data.rows.length === 0 ? (
              <Card>
                <EmptyState title={t('work.departments.empty')} hint={t('common.noResultsHint')} />
              </Card>
            ) : (
              <>
                <Card>
                  <CardHeader title={t('work.departments.chartTitle')} subtitle={t('work.departments.chartSubtitle')} />
                  <ChartFrame label={t('work.departments.chartLabel', { n: num(data.rows.length) })} className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }} barGap={2} barCategoryGap="20%">
                        <CartesianGrid stroke={GRID} vertical={false} />
                        <XAxis dataKey="code" {...AXIS} tickLine={false} interval={0} />
                        <YAxis {...AXIS} width={44} tickLine={false} axisLine={false} allowDecimals={false} />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          itemStyle={tooltipItemStyle}
                          labelStyle={tooltipLabelStyle}
                          cursor={{ fill: 'var(--surface-sunken)' }}
                          labelFormatter={(_label, payload) => (payload?.[0]?.payload as { name?: string } | undefined)?.name ?? String(_label)}
                          formatter={(value) => num(Number(value))}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value: string) => legendText(value)} />
                        <Bar dataKey="backlog" name={names.backlog} fill={SERIES[0]} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                        <Bar dataKey="completed" name={names.completed} fill={SERIES[1]} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                        <Bar dataKey="previous" name={names.previous} fill={SERIES[2]} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartFrame>
                  <Note className="mt-2">{t('work.departments.chartTwin')}</Note>
                </Card>
                <Card padded={false}>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('common.department')}</Th>
                        <Th align="end">{t('work.departments.members')}</Th>
                        <Th align="end">{t('work.departments.openTasks')}</Th>
                        <Th align="end">{t('work.departments.openTickets')}</Th>
                        <Th align="end">{t('work.departments.openQTasks')}</Th>
                        <Th align="end">{t('work.departments.overdue')}</Th>
                        <Th align="end">{t('work.departments.doneTasks')}</Th>
                        <Th align="end">{t('work.departments.doneTickets')}</Th>
                        <Th align="end">{t('work.departments.doneQTasks')}</Th>
                        <Th align="end">{names.completed}</Th>
                        <Th align="end">{names.previous}</Th>
                        <Th align="end">{t('work.departments.change')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <Tr key={r.department.id}>
                          <Td className="min-w-[10rem]">
                            <span className="font-medium">{localized(r.department)}</span>
                            <span dir="ltr" className="ms-1.5 font-mono text-caption text-[var(--text-tertiary)]">
                              {r.department.code}
                            </span>
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.members)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.backlog.tasks)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.backlog.tickets)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.backlog.qTasks)}
                          </Td>
                          <Td align="end" className={r.overdueTasks > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}>
                            {num(r.overdueTasks)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.completed.tasks)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.completed.tickets)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.completed.qTasks)}
                          </Td>
                          <Td align="end" className="tabular font-medium">
                            {num(r.completed.total)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.previousCompleted)}
                          </Td>
                          <Td align="end" className="whitespace-nowrap tabular">
                            {r.changePercent === null ? (
                              <span className="text-caption text-muted">{t('state.noComparison')}</span>
                            ) : (
                              <span dir="ltr">{`${r.changePercent > 0 ? '+' : r.changePercent < 0 ? '−' : ''}${num(Math.abs(r.changePercent), 1)}%`}</span>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </>
            )}
          </div>
        );
      }}
    </Loaded>
  );
}

/* ---------------------------------------------------------------------------------------------- */

function WorkloadTab() {
  const { t } = useI18n();
  const { query } = usePeriod();
  const num = useNumber();
  const scope = useScope();
  const workload = useQuery({
    queryKey: ['executive', 'work', 'workload', scope.params, scope.departmentId, scope.projectId],
    queryFn: () => api.get<{ period: Period; rows: WorkloadRow[]; total: number }>('/executive/work/workload', scope.request),
    placeholderData: keepPreviousData,
  });
  return (
    <Loaded query={workload}>
      {(data) => (
        <div className="space-y-4">
          <Note>{t('work.workload.note')}</Note>
          <Card padded={false}>
            {data.rows.length === 0 ? (
              <EmptyState title={t('work.workload.empty')} hint={t('common.noResultsHint')} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.employee')}</Th>
                    <Th align="end">{t('work.workload.openTasks')}</Th>
                    <Th align="end">{t('work.workload.overdue')}</Th>
                    <Th align="end">{t('work.workload.dueSoon')}</Th>
                    <Th align="end">{t('work.workload.openTickets')}</Th>
                    <Th align="end">{t('work.workload.openQTasks')}</Th>
                    <Th align="end">{t('work.workload.total')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <Tr key={r.user.id}>
                      <Td className="min-w-[12rem]">
                        <PersonLink person={r.user} query={query} />
                        {r.user.jobTitle && <span className="block text-caption text-muted">{r.user.jobTitle}</span>}
                      </Td>
                      <Td align="end" className="tabular">
                        {num(r.openTasks)}
                      </Td>
                      <Td align="end" className={r.overdue > 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular'}>
                        {num(r.overdue)}
                      </Td>
                      <Td align="end" className="tabular">
                        {num(r.dueSoon)}
                      </Td>
                      <Td align="end" className="tabular">
                        {num(r.openTickets)}
                      </Td>
                      <Td align="end" className="tabular">
                        {num(r.openQTasks)}
                      </Td>
                      <Td align="end" className="tabular font-medium">
                        {num(r.openTasks + r.openTickets + r.openQTasks)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
          {data.total > data.rows.length && <Note>{t('work.workload.limit')}</Note>}
        </div>
      )}
    </Loaded>
  );
}
